import { useAppTranslation } from '@hooks/useAppTranslation';
import { useMemo, useState } from 'react';
import { DatePickerModal } from 'react-native-paper-dates';
import { DateFieldShell } from './DateFieldShell';
import { DEFAULT_DATE_PLACEHOLDER, formatDate } from './dateField.utils';

export interface DateFieldProps {
  label?: string;
  value: Date | null | undefined;
  onValueChange: (value: Date | null) => void;
  placeholder?: string;
  required?: boolean;
}

export const DateField = ({
  label,
  value,
  onValueChange,
  placeholder = DEFAULT_DATE_PLACEHOLDER,
  required = false,
}: DateFieldProps): React.ReactElement => {
  const { language } = useAppTranslation();
  const [isPickerVisible, setIsPickerVisible] = useState(false);

  const formattedValue = useMemo(
    () => (value ? formatDate(value, language) : ''),
    [language, value],
  );

  const handleOpenPicker = (): void => {
    setIsPickerVisible(true);
  };

  const handleDismiss = (): void => {
    setIsPickerVisible(false);
  };

  const handleChange = ({ date }: { date: Date | undefined }): void => {
    onValueChange(date ?? null);
  };

  const handleConfirm = ({ date }: { date: Date | undefined }): void => {
    setIsPickerVisible(false);
    onValueChange(date ?? null);
  };

  const handleDelete = (): void => {
    onValueChange(null);
  };

  return (
    <>
      <DateFieldShell
        label={label}
        required={required}
        valueText={formattedValue}
        placeholder={placeholder}
        onPress={handleOpenPicker}
        onDelete={handleDelete}
      />

      <DatePickerModal
        locale={language}
        mode="single"
        visible={isPickerVisible}
        onDismiss={handleDismiss}
        date={value ?? undefined}
        onChange={handleChange}
        onConfirm={handleConfirm}
      />
    </>
  );
};
